import { uniform, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { GravityField } from '../../engine/gravity/GravityField';
import { mulberry32 } from '../../engine/math/rng';
import { type DynamicBody, type PhysicsWorld, RAPIER } from '../../engine/physics/PhysicsWorld';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import type { Frame } from './Attack';
import { BRUTE, CHARGER, CRITTER, type Kind, KNIGHT, RAIDER, SLIME, targetOf } from './enemyKinds';
import { buildEnemyModel, type EnemyModel } from './enemyModels';
import { contactDir, type HitResult, type Target } from './resolve';
import { toScreen } from './screen';

type N = any;

/** Where each stands: angle across the player's view (radians, + right) and distance (m). */
const ROSTER: { kind: Kind; angle: number; dist: number }[] = [
  { kind: KNIGHT, angle: -0.95, dist: 7 },
  { kind: RAIDER, angle: -0.45, dist: 8 },
  { kind: SLIME, angle: 0, dist: 7.5 },
  { kind: BRUTE, angle: 0.5, dist: 10 },
  { kind: CHARGER, angle: 0.95, dist: 13 },
  { kind: CRITTER, angle: -1.4, dist: 8 },
  { kind: CRITTER, angle: -1.5, dist: 9.2 },
  { kind: CRITTER, angle: -1.3, dist: 9.6 },
];

/** How far a shield reaches round from the way its bearer faces, and how far ahead it reads your aim (s). */
const SHIELD_LEAD = 0.2;
const SHIELD_REACH = (80 * Math.PI) / 180;
/** Seconds a beaten enemy stays down, and how far it may be knocked before it's put back (m). */
const RESPAWN = 1.6;
const LEASH = 120;
/** Seconds damage numbers float over the enemies. */
const FLOAT_TIME = 7;
/**
 * Ragdolls: how much a tumbling body's spin is damped, how fast it may still be moving (m/s)
 * to get up once the stun is over (or it gets up anyway RAGDOLL_MAX s after), and how long
 * getting up takes (s).
 */
const RAGDOLL_DAMPING = 3;
/** Most a hit spins a ragdoll (rad/s). */
const MAX_TOPPLE = 5;
/** A limp body drags on the ground more than a staggering one slides. */
const LIMP_GRIP = 0.6;
const REST_SPEED = 1.5;
const RAGDOLL_MAX = 3;
const GET_UP = 0.35;
/** Friction on its feet, and while knocked off balance. */
const GRIP = 0.9;
const SLIDE = 0.25;
/** Charger: seconds between charges, the warning before one, and how far and long it runs. */
const CHARGE_EVERY = 3.5;
const CHARGE_WARN = 0.9;
const CHARGE_RANGE = 20;
const CHARGE_RUN = 16;
const CHARGE_TIME = 2.2;

export interface Enemy {
  kind: Kind;
  hp: number;
  /** Where it stands when (re)spawned: its centre, and its "up". */
  home: THREE.Vector3;
  up: THREE.Vector3;
  body: DynamicBody | null;
  collider: RAPIER.Collider | null;
  /** Which way it faces, and the side its shield covers (unit, across its up). */
  facing: THREE.Vector3;
  shieldDir: THREE.Vector3;
  /** Seconds left stunned, and not driving itself (knocked about). */
  stun: number;
  stagger: number;
  down: number;
  /** Behaviour: a timer, a state, a direction, and the aim-offset reading for the shield. */
  timer: number;
  state: 'idle' | 'warn' | 'run' | 'back';
  moveDir: THREE.Vector3;
  runFrom: THREE.Vector3;
  aimPrev: number;
  aimRate: number;
  group: THREE.Group;
  /** Its model (animated by its speed), and what turns with its shield. */
  model: EnemyModel;
  pivot: THREE.Object3D | null;
  /** Its centre last frame, and how fast it's been moving (m/s, smoothed). */
  last: THREE.Vector3;
  speed: number;
  flash: { value: number };
  stunGlow: { value: number };
  warnGlow: { value: number };
  plate: HTMLDivElement;
  /**
   * Stunned: its body tumbles freely (rotation unlocked). `pose` turns the body's rotation into
   * the model's. Afterwards it gets up over GET_UP seconds, from where it lay (`fallen`, and
   * `lay`, the offset from its standing centre).
   */
  ragdoll: boolean;
  /** Seconds ragdolled since the stun ran out. */
  limp: number;
  pose: THREE.Quaternion;
  getUp: number;
  fallen: THREE.Quaternion;
  lay: THREE.Vector3;
  /** Its centre when the player last released an attack (for its movement during the strike). */
  atRelease: THREE.Vector3;
  fill: HTMLDivElement;
}

const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _qb = new THREE.Quaternion();
const _qu = new THREE.Quaternion();
const _x = new THREE.Vector3();
const _z = new THREE.Vector3();
const _to = new THREE.Vector3();
const _v = new THREE.Vector3();
const _want = new THREE.Vector3();
const _right = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _view = {
  eye: new THREE.Vector3(),
  forward: new THREE.Vector3(),
  up: new THREE.Vector3(),
  feet: new THREE.Vector3(),
};
const _target = { center: new THREE.Vector3(), radius: 0 };

/**
 * Test enemies for the combat prototype: physics bodies of different builds, each meant to
 * reward one of the bonuses (finesse, rotation, movement). They move about (hop, skitter,
 * strafe, charge) but never attack. A knight's shield reads your swing and moves to where it's
 * heading, so only a late change of direction gets round it. Health bars, names and
 * damage numbers are HTML over the view.
 */
export class Enemies {
  readonly list: Enemy[] = [];
  private readonly overlay: HTMLDivElement;
  private readonly floaters: { el: HTMLDivElement; at: THREE.Vector3; age: number }[] = [];
  /** Repeatable behaviour between runs. */
  private readonly random = mulberry32(1);

  constructor(
    private readonly physics: PhysicsWorld,
    private readonly field: GravityField,
    private readonly origin: RenderOrigin,
    private readonly scene: THREE.Scene,
  ) {
    this.overlay = document.createElement('div');
    this.overlay.id = 'combat-overlay';
    this.overlay.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:5';
    document.body.appendChild(this.overlay);
  }

  /**
   * Stand the enemies in an arc in front of `feet` (facing back toward it), each on the
   * ground found below its spot. Returns false while the ground there has no collision yet.
   */
  spawn(feet: THREE.Vector3, up: THREE.Vector3, forward: THREE.Vector3): boolean {
    const f = _z.copy(forward).addScaledVector(up, -forward.dot(up)).normalize();
    const side = _x.crossVectors(f, up).normalize();
    const spots = ROSTER.map(({ angle, dist }) =>
      feet
        .clone()
        .addScaledVector(f, dist * Math.cos(angle))
        .addScaledVector(side, dist * Math.sin(angle)),
    );
    const grounds = spots.map((s) => this.ground(s, up));
    if (grounds.some((g) => g === null)) return false;
    ROSTER.forEach(({ kind }, i) => {
      const home = (grounds[i] as THREE.Vector3).addScaledVector(up, kind.height / 2 + 0.02);
      this.list.push(this.make(kind, home, up.clone(), feet));
    });
    return true;
  }

  private make(kind: Kind, home: THREE.Vector3, up: THREE.Vector3, facing: THREE.Vector3): Enemy {
    const flash = uniform(0);
    const stunGlow = uniform(0);
    const warnGlow = uniform(0);
    const glow = vec3(1, 0.85, 0.7)
      .mul(flash as N)
      .add(vec3(0.9, 0.7, 0.1).mul(stunGlow as N))
      .add(vec3(1, 0.12, 0.04).mul(warnGlow as N));
    const model = buildEnemyModel(kind, glow);
    const group = model.group;
    group.name = `enemy ${kind.name}`;
    const pivot = kind.shield ? model.shield : null;
    group.traverse((o) => {
      o.frustumCulled = false;
    });
    this.scene.add(group);
    const plate = document.createElement('div');
    plate.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);font:600 11px system-ui,sans-serif;color:#f1ece0;text-shadow:0 1px 2px #000;text-align:center;white-space:nowrap;display:none';
    const title = document.createElement('div');
    title.textContent = kind.name;
    const tip = document.createElement('div');
    tip.textContent = kind.tip;
    tip.style.cssText = 'font-weight:400;opacity:.75';
    const bar = document.createElement('div');
    bar.style.cssText =
      'width:64px;height:6px;margin:2px auto 0;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.35);border-radius:3px';
    const fill = document.createElement('div');
    fill.style.cssText = 'height:100%;width:100%;background:#e0503a;border-radius:2px';
    bar.appendChild(fill);
    plate.append(title, tip, bar);
    this.overlay.appendChild(plate);
    const face = _to.subVectors(facing, home);
    face.addScaledVector(up, -face.dot(up)).normalize();
    const e: Enemy = {
      kind,
      hp: kind.hp,
      home: home.clone(),
      up,
      body: null,
      collider: null,
      facing: face.clone(),
      shieldDir: face.clone(),
      stun: 0,
      stagger: 0,
      down: 0,
      timer: 0,
      state: 'idle',
      moveDir: face.clone(),
      runFrom: home.clone(),
      aimPrev: 0,
      aimRate: 0,
      group,
      model,
      pivot,
      last: home.clone(),
      speed: 0,
      flash,
      stunGlow,
      warnGlow,
      plate,
      ragdoll: false,
      limp: 0,
      pose: new THREE.Quaternion(),
      getUp: 0,
      fallen: new THREE.Quaternion(),
      lay: new THREE.Vector3(),
      atRelease: new THREE.Vector3(),
      fill,
    };
    this.place(e);
    return e;
  }

  /** (Re)create the enemy's body at home, at rest, full health. */
  private place(e: Enemy): void {
    if (e.body) this.physics.removeDynamic(e.body);
    this.physics.toLocal(e.home, _p);
    // The capsule stands along its up (+Y rotated onto up); rotation is locked, so it stays upright.
    _q.setFromUnitVectors(_tmp.set(0, 1, 0), e.up);
    const rb = this.physics.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(_p.x, _p.y, _p.z)
        .setRotation({ x: _q.x, y: _q.y, z: _q.z, w: _q.w })
        .lockRotations()
        .setLinearDamping(0.15)
        .setCcdEnabled(true),
    );
    const k = e.kind;
    const shape =
      k.height <= 2 * k.radius + 1e-3
        ? RAPIER.ColliderDesc.ball(k.radius)
        : RAPIER.ColliderDesc.capsule(k.height / 2 - k.radius, k.radius);
    e.collider = this.physics.world.createCollider(shape.setMass(k.mass).setFriction(GRIP), rb);
    e.body = this.physics.addDynamic(rb);
    e.hp = k.hp;
    e.down = 0;
    e.stun = 0;
    e.stagger = 0;
    e.state = 'idle';
    e.timer = this.random() * CHARGE_EVERY;
    e.ragdoll = false;
    e.getUp = 0;
    e.atRelease.copy(e.home);
    e.group.visible = true;
  }

  /** Put every enemy back where it started. */
  reset(): void {
    for (const e of this.list) this.place(e);
  }

  /** The ground below `p` (along -up), from the static colliders, or null. */
  private ground(p: THREE.Vector3, up: THREE.Vector3): THREE.Vector3 | null {
    const w = this.physics.world;
    this.physics.toLocal(p, _p).addScaledVector(up, 4);
    const ray = new RAPIER.Ray({ x: _p.x, y: _p.y, z: _p.z }, { x: -up.x, y: -up.y, z: -up.z });
    const hit = w.castRay(ray, 12, true, undefined, undefined, undefined, undefined, (c) =>
      Boolean(c.parent()?.isFixed()),
    );
    if (!hit) return null;
    return p.clone().addScaledVector(up, 4 - hit.timeOfImpact);
  }

  /** Universe centre and velocity of an enemy's body. */
  center(e: Enemy, out: THREE.Vector3): THREE.Vector3 {
    return e.body ? this.physics.toUniverse(e.body.body.translation(), out) : out.copy(e.home);
  }

  /** The player released an attack: remember where everyone is, for their movement during the strike. */
  markRelease(): void {
    for (const e of this.list) this.center(e, e.atRelease);
  }

  /** What a hit needs to know about it (the shield is down while stunned). */
  target(e: Enemy): Target {
    const center = this.center(e, new THREE.Vector3());
    return targetOf(
      e.kind,
      center,
      center.clone().sub(e.atRelease),
      e.stun <= 0 ? e.shieldDir.clone() : null,
    );
  }

  /** Physics step: behaviours, facing, shields reading the player's aim, respawns. */
  update(dt: number, player: Frame): void {
    for (const e of this.list) {
      if (!e.body) continue;
      this.center(e, _p);
      if (e.down > 0) {
        e.down -= dt;
        if (e.down <= 0) this.place(e);
        continue;
      }
      if (_p.distanceTo(e.home) > LEASH) {
        this.place(e);
        continue;
      }
      // Its up follows the local gravity (the body stays as spawned; only the look turns).
      this.field.sample(_p, _v);
      if (_v.lengthSq() > 1e-6) e.up.copy(_v).normalize().negate();
      e.stun = Math.max(0, e.stun - dt);
      e.getUp = Math.max(0, e.getUp - dt);
      if (e.ragdoll) {
        if (e.stun > 0) continue;
        e.limp += dt;
        const v = e.body.body.linvel();
        const w = e.body.body.angvel();
        const moving = Math.hypot(v.x, v.y, v.z) > REST_SPEED || Math.hypot(w.x, w.y, w.z) > 2;
        if (moving && e.limp < RAGDOLL_MAX) continue;
        this.standUp(e);
      }
      if (e.stagger > 0 && e.stagger <= dt) e.collider?.setFriction(GRIP);
      e.stagger = Math.max(0, e.stagger - dt);
      e.warnGlow.value = e.state === 'warn' ? 0.6 + 0.4 * Math.sin(e.timer * 30) : 0;
      if (e.stun > 0) continue;
      _to.subVectors(player.feet, _p);
      _to.addScaledVector(e.up, -_to.dot(e.up));
      const toPlayerDist = _to.length();
      if (toPlayerDist > 1e-3) _to.multiplyScalar(1 / toPlayerDist);
      this.behave(e, dt, toPlayerDist);
      // Face the player, or the way it's running.
      const look = e.state === 'run' || e.kind.behaviour === 'skitter' ? e.moveDir : _to;
      turnToward(e.facing, look, e.up, e.kind.turn * dt);
      if (e.kind.shield) this.readAim(e, dt, player);
    }
  }

  /** Move about as its kind does (unless knocked about). */
  private behave(e: Enemy, dt: number, toPlayerDist: number): void {
    const k = e.kind;
    const up = e.up;
    _want.set(0, 0, 0);
    e.timer -= dt;
    // Home, across up.
    const home = _tmp.subVectors(e.home, _p);
    home.addScaledVector(up, -home.dot(up));
    const fromHome = home.length();
    switch (k.behaviour) {
      case 'stand':
        break;
      case 'hop':
        if (e.timer <= 0 && e.stagger <= 0) {
          e.timer = 1.3 + this.random() * 0.8;
          // A hop somewhere near home.
          const b = e.body!.body;
          const v = b.linvel();
          _v.copy(home)
            .normalize()
            .multiplyScalar(fromHome > 2.5 ? k.speed : 0);
          _x.crossVectors(up, e.facing).multiplyScalar((this.random() - 0.5) * 2 * k.speed);
          _v.add(_x).addScaledVector(up, 3.2);
          b.setLinvel({ x: v.x + _v.x, y: v.y + _v.y, z: v.z + _v.z }, true);
        }
        return;
      case 'skitter':
        if (e.timer <= 0) {
          e.timer = 0.35 + this.random() * 0.7;
          const a = this.random() * Math.PI * 2;
          _x.crossVectors(up, e.facing).normalize();
          e.moveDir.copy(e.facing).multiplyScalar(Math.cos(a)).addScaledVector(_x, Math.sin(a)).normalize();
        }
        if (fromHome > 3.5) e.moveDir.copy(home).normalize();
        _want.copy(e.moveDir).multiplyScalar(k.speed);
        break;
      case 'strafe':
        if (e.timer <= 0) {
          e.timer = 1.2 + this.random() * 0.8;
          e.moveDir.crossVectors(up, _to).normalize();
          if (this.random() < 0.5) e.moveDir.negate();
        }
        if (fromHome > 4 && e.moveDir.dot(home) < 0) e.moveDir.negate();
        _want.copy(e.moveDir).multiplyScalar(k.speed);
        break;
      case 'charge':
        if (e.state === 'idle') {
          if (fromHome > 1.5) _want.copy(home).normalize().multiplyScalar(2.5);
          if (e.timer <= 0 && toPlayerDist < CHARGE_RANGE && toPlayerDist > 4) {
            e.state = 'warn';
            e.timer = CHARGE_WARN;
          }
        } else if (e.state === 'warn') {
          if (e.timer <= 0) {
            e.state = 'run';
            e.timer = CHARGE_TIME;
            e.moveDir.copy(_to);
            e.runFrom.copy(_p);
          }
        } else if (e.state === 'run') {
          _want.copy(e.moveDir).multiplyScalar(k.speed);
          const ran = _p.distanceTo(e.runFrom);
          if (e.timer <= 0 || ran > CHARGE_RUN || toPlayerDist < 1.4 + k.radius) {
            e.state = 'idle';
            e.timer = CHARGE_EVERY;
          }
        }
        break;
    }
    // Knocked about: let it slide. Otherwise walk back home when pushed away.
    if (e.stagger > 0) return;
    if (k.behaviour === 'stand' && fromHome > 1)
      _want.copy(home).normalize().multiplyScalar(Math.min(k.speed, fromHome));
    const b = e.body!.body;
    const lv = b.linvel();
    _v.set(lv.x, lv.y, lv.z);
    const along = _v.dot(up);
    _x.copy(_v).addScaledVector(up, -along);
    _x.lerp(_want, 1 - Math.exp(-10 * dt));
    _x.addScaledVector(up, along);
    b.setLinvel({ x: _x.x, y: _x.y, z: _x.z }, true);
  }

  /**
   * A shield moves to where the player's attack is heading: where their aim will pass the
   * enemy a moment ahead. A smooth swing is read and met; a late reversal (or stopping short)
   * lands where the shield isn't, if it's aimed there.
   */
  private readAim(e: Enemy, dt: number, f: Frame): void {
    const shield = e.kind.shield!;
    _right.copy(f.forward).addScaledVector(f.up, -f.forward.dot(f.up));
    if (_right.lengthSq() < 1e-6) return;
    _right.normalize().cross(f.up).normalize();
    const offset = -_v.subVectors(_p, f.eye).dot(_right);
    const rate = dt > 0 ? (offset - e.aimPrev) / dt : 0;
    e.aimRate += (rate - e.aimRate) * (1 - Math.exp(-dt / 0.03));
    e.aimPrev = offset;
    _view.eye.copy(f.eye);
    _view.forward.copy(f.forward);
    _view.up.copy(e.up);
    _view.feet.copy(f.feet);
    _target.center.copy(_p);
    _target.radius = e.kind.radius;
    const want = contactDir(_view, _target, SHIELD_LEAD * e.aimRate, _want);
    // Only so far round from where it faces.
    const a = signedAngle(e.facing, want, e.up);
    const clamped = Math.max(-SHIELD_REACH, Math.min(SHIELD_REACH, a));
    want.copy(e.facing).applyAxisAngle(e.up, clamped);
    turnToward(e.shieldDir, want, e.up, shield.turn * dt);
  }

  /** The model's rotation standing upright on its up, facing its way (models face -Z). */
  private upright(e: Enemy, out: THREE.Quaternion): THREE.Quaternion {
    _z.copy(e.facing).negate();
    _x.crossVectors(e.up, _z).normalize();
    _m.makeBasis(_x, e.up, _z.crossVectors(_x, e.up));
    return out.setFromRotationMatrix(_m);
  }

  /** The model's rotation while ragdolled: the body's, turned by its pose. */
  private ragdollPose(e: Enemy, out: THREE.Quaternion): THREE.Quaternion {
    const r = e.body!.body.rotation();
    return out.set(r.x, r.y, r.z, r.w).multiply(e.pose);
  }

  /** Go limp: the body may tumble, tipped over the way it was hit. */
  private goLimp(e: Enemy, push: THREE.Vector3): void {
    const b = e.body!.body;
    if (!e.ragdoll) {
      // Keep the model where it is: pose = body⁻¹ · model.
      const r = b.rotation();
      _qb.set(r.x, r.y, r.z, r.w).invert();
      e.pose.copy(_qb.multiply(this.upright(e, _qu)));
      b.lockRotations(false, true);
      b.setAngularDamping(RAGDOLL_DAMPING);
      e.collider?.setFriction(LIMP_GRIP);
      e.ragdoll = true;
      e.getUp = 0;
    }
    e.limp = 0;
    // Topple away from the push (it pushes the body low, the head goes over), plus a little
    // tumble so an uppercut or a straight push doesn't just slide it upright.
    const tip = _v.crossVectors(e.up, push).multiplyScalar(0.6 / e.kind.height);
    if (tip.length() > MAX_TOPPLE) tip.setLength(MAX_TOPPLE);
    const w = b.angvel();
    const spin = 0.8 * (this.random() - 0.5);
    b.setAngvel(
      {
        x: w.x + tip.x + e.facing.x * spin,
        y: w.y + tip.y + e.facing.y * spin,
        z: w.z + tip.z + e.facing.z * spin,
      },
      true,
    );
  }

  /** The stun is over and it's come to rest: stand it back up where it lies. */
  private standUp(e: Enemy): void {
    const b = e.body!.body;
    this.ragdollPose(e, e.fallen);
    const at = this.center(e, new THREE.Vector3());
    // Stand on the ground under it (or just upright where it is, if there's none near).
    const ground = this.ground(at, e.up);
    const standing =
      ground && ground.distanceTo(at) < e.kind.height + 1
        ? ground.addScaledVector(e.up, e.kind.height / 2 + 0.02)
        : at.clone();
    e.lay.subVectors(at, standing);
    this.physics.toLocal(standing, _v);
    _qb.setFromUnitVectors(_tmp.set(0, 1, 0), e.up);
    b.setAngvel({ x: 0, y: 0, z: 0 }, true);
    b.setRotation({ x: _qb.x, y: _qb.y, z: _qb.z, w: _qb.w }, true);
    b.setTranslation({ x: _v.x, y: _v.y, z: _v.z }, true);
    b.setLinvel({ x: 0, y: 0, z: 0 }, true);
    b.lockRotations(true, true);
    b.setAngularDamping(0);
    e.ragdoll = false;
    e.getUp = GET_UP;
    e.stagger = GET_UP;
    e.collider?.setFriction(GRIP);
  }

  /** Apply a resolved hit: damage, knockback, stun, a flash and a floating number. */
  hit(e: Enemy, r: HitResult, at: THREE.Vector3, label: string): void {
    if (!e.body || e.down > 0) return;
    const b = e.body.body;
    const v = b.linvel();
    b.setLinvel({ x: v.x + r.push.x, y: v.y + r.push.y, z: v.z + r.push.z }, true);
    e.hp -= r.damage;
    e.stun = Math.max(e.stun, r.stun);
    // Stunned: it goes limp and tumbles for as long as the stun lasts (and until it lands).
    if (e.stun > 0 && e.hp > 0) this.goLimp(e, r.push);
    // Knocked about: it slides while it's off balance, the harder the hit the longer.
    const knock = r.push.length();
    e.stagger = Math.max(e.stagger, r.stun, 0.2 + knock / 12);
    if (knock > 2 && !e.ragdoll) e.collider?.setFriction(SLIDE);
    if (r.stun > 0 && e.state !== 'idle') {
      e.state = 'idle';
      e.timer = CHARGE_EVERY;
    }
    e.flash.value = 1;
    const color = r.blocked ? '#9fb8d6' : r.crit > 0 ? '#ffd24a' : r.damage >= 50 ? '#ffcf5a' : '#ffffff';
    this.float(at, label, color);
    if (r.blocked) this.float(at.clone().addScaledVector(e.up, 0.45), 'Blocked!', '#b9d4f2', 24);
    else if (r.crit > 0) this.float(at.clone().addScaledVector(e.up, 0.45), 'Critical!', '#ffcc33', 26);
    if (e.hp <= 0) {
      e.down = RESPAWN;
      e.group.visible = false;
      e.plate.style.display = 'none';
      this.float(at.clone().addScaledVector(e.up, 0.5), 'down!', '#ff8a6a');
    } else if (r.stun > 0) this.float(at.clone().addScaledVector(e.up, 0.7), 'stunned', '#ffd24a');
  }

  private float(at: THREE.Vector3, text: string, color: string, size = 18): void {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = `position:absolute;transform:translate(-50%,-50%);font:${size > 18 ? 800 : 600} ${size}px system-ui,sans-serif;color:${color};text-shadow:0 1px 3px #000,0 0 8px rgba(0,0,0,.6);white-space:pre;text-align:center`;
    this.overlay.appendChild(el);
    this.floaters.push({ el, at: at.clone(), age: 0 });
  }

  /** Per frame: place the enemies and their plates camera-relative, fade flashes and numbers. */
  frame(dt: number, camera: THREE.Camera, visible: boolean): void {
    this.overlay.style.display = visible ? '' : 'none';
    camera.updateMatrixWorld();
    for (const e of this.list) {
      e.flash.value = Math.max(0, e.flash.value - dt * 6);
      e.stunGlow.value = e.stun > 0 ? 0.35 + 0.25 * Math.sin(performance.now() / 90) : 0;
      if (!e.body || e.down > 0) continue;
      this.center(e, _p);
      if (dt > 0) {
        const v = e.last.distanceTo(_p) / dt;
        e.speed += (Math.min(v, 30) - e.speed) * (1 - Math.exp(-dt * 10));
      }
      e.last.copy(_p);
      e.model.update(dt, e.ragdoll ? 0 : e.speed);
      if (e.ragdoll) this.ragdollPose(e, e.group.quaternion);
      else if (e.getUp > 0) {
        // Getting up: from how it lay to standing.
        const k = e.getUp / GET_UP;
        const s = k * k * (3 - 2 * k);
        e.group.quaternion.slerpQuaternions(this.upright(e, _qu), e.fallen, s);
        _p.addScaledVector(e.lay, s);
      } else this.upright(e, e.group.quaternion);
      this.origin.toRender(_p, _p);
      e.group.position.copy(_p);
      if (e.pivot) {
        // The shield round its bearer to the side it covers; lowered while stunned.
        e.pivot.rotation.set(e.stun > 0 ? 0.9 : 0, signedAngle(e.facing, e.shieldDir, e.up), 0);
      }
      // Name, what works on it, and health, above the head.
      const near = _p.length() < 30;
      const show = near && toScreen(_tmp.copy(_p).addScaledVector(e.up, e.kind.height / 2 + 0.3), camera, _v);
      e.plate.style.display = show ? '' : 'none';
      if (show) {
        e.plate.style.left = `${((_v.x + 1) / 2) * 100}%`;
        e.plate.style.top = `${((1 - _v.y) / 2) * 100}%`;
        e.fill.style.width = `${Math.max(0, (e.hp / e.kind.hp) * 100)}%`;
      }
    }
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i];
      f.age += dt;
      if (f.age > FLOAT_TIME) {
        f.el.remove();
        this.floaters.splice(i, 1);
        continue;
      }
      const seen = toScreen(this.origin.toRender(f.at, _tmp), camera, _v);
      f.el.style.display = seen ? '' : 'none';
      f.el.style.left = `${((_v.x + 1) / 2) * 100}%`;
      // Rising slowly, fading over its last seconds.
      const k = f.age / FLOAT_TIME;
      f.el.style.top = `calc(${((1 - _v.y) / 2) * 100}% - ${k * 60}px)`;
      f.el.style.opacity = `${Math.min(1, 3.5 * (1 - k))}`;
    }
  }
}

/** Angle from `a` to `b` about `up` (radians, + counter-clockwise seen from above). */
function signedAngle(a: THREE.Vector3, b: THREE.Vector3, up: THREE.Vector3): number {
  return Math.atan2(_tmp.crossVectors(a, b).dot(up), a.dot(b));
}

/** Turn unit `dir` about `up` toward `goal` by at most `step` radians, kept across up. */
function turnToward(dir: THREE.Vector3, goal: THREE.Vector3, up: THREE.Vector3, step: number): void {
  const angle = signedAngle(dir, goal, up);
  dir.applyAxisAngle(up, Math.sign(angle) * Math.min(Math.abs(angle), step));
  dir.addScaledVector(up, -dir.dot(up)).normalize();
}
